import { Recapt } from '../engine';
import { RequestManager } from '../managers/requestManager';
import { BaseEvent, BaseModule } from './base';
import { Namespace } from './types';

const MAX_EVENT_NAME_LENGTH = 100;
const MAX_PROPERTY_KEY_LENGTH = 50;
const MAX_PROPERTY_VALUE_LENGTH = 500;
const MAX_PROPERTIES_COUNT = 20;

export interface CustomEventData extends BaseEvent {
  event: string;
  properties?: Record<string, string | number | boolean | null>;
}

/**
 * Sanitize a string to be safe for use as a field name.
 * - Removes $ prefix (MongoDB operator injection prevention)
 * - Replaces . with _ (nested document traversal prevention)
 */
function sanitizeFieldName(key: string, maxLength: number): string | null {
  if (typeof key !== 'string') return null;

  let sanitized = key.trim();

  // Remove leading $ to prevent operator injection
  while (sanitized.startsWith('$')) {
    sanitized = sanitized.slice(1);
  }

  // Replace dots to prevent nested document traversal
  sanitized = sanitized.replace(/\./g, '_');

  // Limit length
  sanitized = sanitized.slice(0, maxLength);

  return sanitized.length > 0 ? sanitized : null;
}

export class CustomEventsModule extends BaseModule<CustomEventData> {
  public namespace: Namespace = 'customEvents';

  onCustomEvent(
    event: string,
    properties?: Record<string, string | number | boolean | null>,
  ) {
    if (!event || typeof event !== 'string') {
      console.warn('[Recapt] onCustomEvent: invalid event name');
      return;
    }

    const sanitizedEvent = sanitizeFieldName(event, MAX_EVENT_NAME_LENGTH);
    if (!sanitizedEvent) {
      console.warn('[Recapt] onCustomEvent: event name sanitized to empty');
      return;
    }

    const sanitizedProperties = this.sanitizeProperties(properties);

    RequestManager.potentialPainpoint();

    const eventData = {
      timestamp: Recapt.getCurrentSessionTimestamp(),
      event: sanitizedEvent,
      properties: sanitizedProperties,
      where: Recapt.where(),
    };

    this.state.push(eventData);

    console.debug(
      '[Recapt] Custom event added to state:',
      eventData,
      'State length:',
      this.state.length,
    );
  }

  private sanitizeProperties(
    properties?: Record<string, string | number | boolean | null>,
  ): Record<string, string | number | boolean | null> | undefined {
    if (
      !properties ||
      typeof properties !== 'object' ||
      Array.isArray(properties)
    ) {
      return undefined;
    }

    const sanitized: Record<string, string | number | boolean | null> = {};
    let count = 0;

    for (const [key, value] of Object.entries(properties)) {
      if (count >= MAX_PROPERTIES_COUNT) break;

      const sanitizedKey = sanitizeFieldName(key, MAX_PROPERTY_KEY_LENGTH);
      if (!sanitizedKey) continue;

      if (value === null) {
        sanitized[sanitizedKey] = null;
      } else if (typeof value === 'boolean') {
        sanitized[sanitizedKey] = value;
      } else if (typeof value === 'number' && Number.isFinite(value)) {
        sanitized[sanitizedKey] = value;
      } else if (typeof value === 'string') {
        sanitized[sanitizedKey] = value.slice(0, MAX_PROPERTY_VALUE_LENGTH);
      }
      // Skip any other types (objects, arrays, functions, undefined, etc.)

      count++;
    }

    return Object.keys(sanitized).length > 0 ? sanitized : undefined;
  }

  onStart() {
    const stored = this.loadStore<{
      state: CustomEventData[];
    }>();

    if (stored && Array.isArray(stored.state)) {
      // Push stored items to the existing (patched) state array
      // instead of replacing it, to preserve the patched push method
      for (const item of stored.state) {
        this.state.push(item);
      }
    }

    this.store(() => ({
      state: this.state,
    }));
  }

  onResume(): void {}

  onStop() {}

  purgeState(): void {
    this.state = [];
  }

  buildRequest(
    state: CustomEventData[],
  ): Record<string, unknown> & { state: CustomEventData[] } {
    return {
      state,
    };
  }
}
